/**
 * Collection Sort by Availability
 *
 * Ensures sold-out products appear only on the last pages of a collection.
 * Available products fill pages first; sold-out products accumulate at the end.
 *
 * How it works:
 * 1. Fetches all paginated section pages via Section Rendering API (parallel)
 * 2. Extracts <product-item> cards from each page (preserves full Liquid rendering)
 * 3. Sorts: available products first, sold-out products last
 * 4. Implements virtual client-side pagination
 * 5. Integrates with the theme's ProductFacet for filter/sort/pagination changes
 */
(function () {
  'use strict';

  var PRODUCT_LIST_SEL = '.product-list__inner';
  var PRODUCT_ITEM_TAG = 'product-item';
  var SOLD_OUT_CLASS = 'product-item--sold-out';
  var MAX_FETCHABLE_PAGES = 20;

  /* ───────────────────────── State ───────────────────────── */

  var state = {
    sortedCards: [],
    currentPage: 1,
    perPage: 24,
    sectionId: '',
    totalServerPages: 1,
    initialized: false,
    fetching: false,
    generation: 0,
    arrowPrevSvg: '',
    arrowNextSvg: '',
    abortController: null
  };

  /* ───────────────────────── Helpers ───────────────────────── */

  function getPageFromUrl() {
    return parseInt(new URL(window.location.href).searchParams.get('page'), 10) || 1;
  }

  function isCollectionPath(url) {
    return url.pathname.indexOf('/collections/') === 0;
  }

  function normalizeCollectionUrl(url) {
    var normalized = new URL(url, window.location.origin);

    if (isCollectionPath(normalized)) {
      normalized.searchParams.set('filter.v.availability', '1');
    }

    return normalized;
  }

  function normalizeFacetEventUrl(event) {
    if (!event || !event.detail || !event.detail.url) return;
    event.detail.url = normalizeCollectionUrl(event.detail.url).toString();
  }

  function showLoading() {
    var el = document.querySelector('product-facet');
    if (el && typeof el.showLoadingBar === 'function') el.showLoadingBar();
  }

  function hideLoading() {
    var el = document.querySelector('product-facet');
    if (el && typeof el.hideLoadingBar === 'function') el.hideLoadingBar();
  }

  function scrollToTop() {
    var fm = document.getElementById('facet-main');
    var target =
      (fm && fm.querySelector('.product-facet__meta-bar')) ||
      (fm && fm.querySelector('.product-facet__product-list')) ||
      fm;
    if (target) {
      requestAnimationFrame(function () {
        target.scrollIntoView({ block: 'start', behavior: 'smooth' });
      });
    }
  }

  /** Capture arrow SVGs from the existing pagination before any DOM changes */
  function captureArrowIcons() {
    var fm = document.getElementById('facet-main');
    if (!fm) return;
    var pagination = fm.querySelector('page-pagination');
    if (!pagination) return;

    var prev = pagination.querySelector('[rel="prev"]');
    var next = pagination.querySelector('[rel="next"]');
    if (prev) {
      var svg = prev.querySelector('svg');
      if (svg) state.arrowPrevSvg = svg.outerHTML;
    }
    if (next) {
      var svg = next.querySelector('svg');
      if (svg) state.arrowNextSvg = svg.outerHTML;
    }
  }

  /* ───────────────────────── Core Logic ───────────────────────── */

  /** Reorder product items within the current page DOM (single-page / fallback) */
  function reorderCurrentPageDOM() {
    var fm = document.getElementById('facet-main');
    if (!fm) return;
    var list = fm.querySelector(PRODUCT_LIST_SEL);
    if (!list) return;

    var items = Array.from(list.querySelectorAll(':scope > ' + PRODUCT_ITEM_TAG));
    var avail = items.filter(function (el) { return !el.classList.contains(SOLD_OUT_CLASS); });
    var sold  = items.filter(function (el) { return  el.classList.contains(SOLD_OUT_CLASS); });

    avail.concat(sold).forEach(function (el) {
      el.style.opacity = '1';
      el.style.visibility = 'visible';
      list.appendChild(el);
    });
  }

  /** Abort any in-flight fetchAllPages requests */
  function abortFetchAllPages() {
    if (state.abortController) {
      state.abortController.abort();
      state.abortController = null;
    }
  }

  /** Fetch all server pages in parallel via Section Rendering API */
  async function fetchAllPages(sectionId, totalPages) {
    abortFetchAllPages();
    state.abortController = new AbortController();
    var signal = state.abortController.signal;

    var baseUrl = normalizeCollectionUrl(window.location.href);
    baseUrl.searchParams.set('section_id', sectionId);

    var promises = [];
    for (var p = 1; p <= totalPages; p++) {
      (function (page) {
        var url = new URL(baseUrl);
        url.searchParams.set('page', String(page));
        promises.push(
          fetch(url.toString(), { signal: signal }).then(function (r) { return r.text(); })
        );
      })(p);
    }

    return Promise.all(promises);
  }

  /** Extract product cards from a section HTML string */
  function extractCards(html) {
    var div = document.createElement('div');
    div.innerHTML = html;
    var list = div.querySelector(PRODUCT_LIST_SEL);
    if (!list) {
      console.warn('[CollectionSort] Could not find', PRODUCT_LIST_SEL, 'in fetched HTML');
      return [];
    }

    var cards = [];
    var items = list.querySelectorAll(':scope > ' + PRODUCT_ITEM_TAG);
    // Fallback: if :scope selector fails, try without it
    if (items.length === 0) {
      items = list.querySelectorAll(PRODUCT_ITEM_TAG);
    }
    items.forEach(function (item) {
      cards.push({
        html: item.outerHTML,
        soldOut: item.classList.contains(SOLD_OUT_CLASS)
      });
    });
    return cards;
  }

  /** Sort cards: available first, sold-out last (preserves original order within each group) */
  function sortCards(cards) {
    var avail = cards.filter(function (c) { return !c.soldOut; });
    var sold  = cards.filter(function (c) { return  c.soldOut; });
    return avail.concat(sold);
  }

  /** Render a virtual page into the product grid */
  function renderPage(pageNum) {
    var fm = document.getElementById('facet-main');
    if (!fm) return;
    var list = fm.querySelector(PRODUCT_LIST_SEL);
    if (!list) return;

    var start = (pageNum - 1) * state.perPage;
    var end = Math.min(start + state.perPage, state.sortedCards.length);
    var pageCards = state.sortedCards.slice(start, end);

    // GUARD: never clear the grid if we have no cards to show
    if (pageCards.length === 0) {
      console.warn('[CollectionSort] No cards for page', pageNum, '- keeping existing content');
      return;
    }

    var html = pageCards.map(function (c) { return c.html; }).join('');
    list.innerHTML = html;

    // Force visibility on all inserted items (counteracts stagger-apparition animation)
    var insertedItems = list.querySelectorAll(PRODUCT_ITEM_TAG);
    insertedItems.forEach(function (item) {
      item.style.opacity = '1';
      item.style.visibility = 'visible';
      item.removeAttribute('reveal');
    });

    // Also update the parent product-list's cached productItems reference
    var productListEl = list.closest('product-list');
    if (productListEl) {
      productListEl.productItems = Array.from(insertedItems);
    }
  }

  /** Build virtual pagination matching the theme's pagination structure */
  function buildPagination(currentPage) {
    var fm = document.getElementById('facet-main');
    if (!fm) return;

    var paginationEl = fm.querySelector('page-pagination');
    var totalPages = Math.ceil(state.sortedCards.length / state.perPage);

    if (totalPages <= 1) {
      if (paginationEl) paginationEl.innerHTML = '';
      return;
    }

    // Create pagination element if it doesn't exist (e.g. was single-page server-side)
    if (!paginationEl) {
      paginationEl = document.createElement('page-pagination');
      paginationEl.setAttribute('ajax', '');
      paginationEl.classList.add('pagination');
      var productListEl = fm.querySelector('.product-facet__product-list');
      if (productListEl) {
        productListEl.insertAdjacentElement('afterend', paginationEl);
      }
    }

    // Build page parts with ellipsis for large page counts
    var WINDOW = 2;
    var parts = [];
    for (var i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - WINDOW && i <= currentPage + WINDOW)) {
        parts.push(i);
      } else if (parts.length > 0 && parts[parts.length - 1] !== 0) {
        parts.push(0); // 0 = ellipsis placeholder
      }
    }

    // Fallback SVGs (if we couldn't capture them from the existing pagination)
    var prevSvg = state.arrowPrevSvg ||
      '<svg focusable="false" width="17" height="14" viewBox="0 0 17 14"><path d="M17 7H2M8 1L2 7l6 6" stroke="currentColor" stroke-width="2" fill="none"></path></svg>';
    var nextSvg = state.arrowNextSvg ||
      '<svg focusable="false" width="17" height="14" viewBox="0 0 17 14"><path d="M0 7h15M9 1l6 6-6 6" stroke="currentColor" stroke-width="2" fill="none"></path></svg>';

    var html = '<nav class="pagination__nav">';

    // Previous arrow
    if (currentPage > 1) {
      html += '<a class="pagination__nav-item" rel="prev" data-vpage="' + (currentPage - 1) + '" href="#">';
      html += prevSvg;
      html += '</a>';
    }

    // Page numbers
    parts.forEach(function (part) {
      if (part === 0) {
        html += '<span class="pagination__nav-item heading heading--small">&hellip;</span>';
      } else if (part === currentPage) {
        html += '<span class="pagination__nav-item heading heading--small" aria-current="page">' + part + '</span>';
      } else {
        html += '<a href="#" data-vpage="' + part + '" class="pagination__nav-item heading heading--small">' + part + '</a>';
      }
    });

    // Next arrow
    if (currentPage < totalPages) {
      html += '<a class="pagination__nav-item" rel="next" data-vpage="' + (currentPage + 1) + '" href="#">';
      html += nextSvg;
      html += '</a>';
    }

    html += '</nav>';
    paginationEl.innerHTML = html;

    // Bind virtual page click events
    paginationEl.querySelectorAll('[data-vpage]').forEach(function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        navigateTo(parseInt(link.dataset.vpage, 10));
      });
    });
  }

  /* ───────────────────────── Navigation ───────────────────────── */

  function navigateTo(pageNum) {
    state.currentPage = pageNum;

    // Update URL without triggering popstate
    var url = new URL(window.location.href);
    if (pageNum <= 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', String(pageNum));
    }
    history.replaceState({}, '', url.toString());

    renderPage(pageNum);
    buildPagination(pageNum);
    scrollToTop();
  }

  /* ───────────────────────── Initialization ───────────────────────── */

  async function initVirtualSort() {
    var dataEl = document.querySelector('[data-virtual-sort]');
    if (!dataEl) return;

    state.sectionId = dataEl.dataset.sectionId || '';
    state.perPage = parseInt(dataEl.dataset.perPage, 10) || 24;
    state.totalServerPages = parseInt(dataEl.dataset.totalServerPages, 10) || 1;
    state.currentPage = getPageFromUrl();
    state.initialized = false;
    state.sortedCards = [];

    // Capture existing arrow icons before DOM changes
    captureArrowIcons();

    // Single page: reorder DOM directly (no API calls)
    if (state.totalServerPages <= 1) {
      reorderCurrentPageDOM();
      state.initialized = true;
      hideLoading();
      return;
    }

    // Too many pages: reorder current page only (performance guard)
    if (state.totalServerPages > MAX_FETCHABLE_PAGES) {
      reorderCurrentPageDOM();
      state.initialized = true;
      hideLoading();
      return;
    }

    state.fetching = true;
    state.generation++;
    var myGeneration = state.generation;
    showLoading();

    try {
      var htmlPages = await fetchAllPages(state.sectionId, state.totalServerPages);

      // Abort if a newer initVirtualSort was triggered (sort/filter changed again)
      if (myGeneration !== state.generation) return;

      // Extract and sort all cards across all pages
      var allCards = [];
      htmlPages.forEach(function (html) {
        allCards = allCards.concat(extractCards(html));
      });
      state.sortedCards = sortCards(allCards);

      // GUARD: if no cards were extracted, fall back to DOM reorder
      if (state.sortedCards.length === 0) {
        console.warn('[CollectionSort] No cards extracted from any page, falling back to DOM reorder');
        reorderCurrentPageDOM();
        state.initialized = true;
        return;
      }

      // Clamp current page to valid range
      var totalVirtualPages = Math.ceil(state.sortedCards.length / state.perPage);
      if (state.currentPage > totalVirtualPages) {
        state.currentPage = Math.max(1, totalVirtualPages);
      }

      renderPage(state.currentPage);
      buildPagination(state.currentPage);
      state.initialized = true;
    } catch (err) {
      if (err.name === 'AbortError' || myGeneration !== state.generation) return;
      console.warn('[CollectionSort] Error fetching pages, using in-page fallback:', err);
      reorderCurrentPageDOM();
      state.initialized = true;
    } finally {
      if (myGeneration === state.generation) {
        state.fetching = false;
        hideLoading();
      }
    }
  }

  /* ───────────────────────── ProductFacet Integration ───────────────────────── */

  function patchProductFacet() {
    var facet = document.querySelector('product-facet');
    if (!facet || facet.__vsPatch) return;

    // Bind original _rerender so we can call it manually after intercepting events.
    // NOTE: We CANNOT patch facet._rerender directly because the delegate already
    // stored a bound reference to the original in connectedCallback. Instead, we
    // intercept events in the CAPTURE phase (runs before delegate's bubble-phase
    // listener) and stopImmediatePropagation to prevent the original handler.
    var originalRerender = facet._rerender.bind(facet);

    // ── Intercept pagination events ──
    facet.addEventListener('pagination:page-changed', function (event) {
      normalizeFacetEventUrl(event);

      // Virtual sort active → navigate client-side (no server request)
      if (state.initialized && state.sortedCards.length > 0) {
        event.stopImmediatePropagation();
        var url = new URL(event.detail.url);
        var page = parseInt(url.searchParams.get('page'), 10) || 1;
        history.replaceState({}, '', event.detail.url);
        navigateTo(page);
        return;
      }

      // Virtual sort not ready yet → intercept, call original, then re-init
      event.stopImmediatePropagation();
      (async function () {
        await originalRerender(event);
        await initVirtualSort();
      })();
    }, true); // capture phase

    // ── Intercept filter / sort change events ──
    facet.addEventListener('facet:criteria-changed', function (event) {
      normalizeFacetEventUrl(event);
      event.stopImmediatePropagation();

      // Immediately invalidate & abort any in-flight virtual sort
      // BEFORE any async work, so stale fetchAllPages results are discarded
      state.initialized = false;
      state.sortedCards = [];
      state.generation++;
      abortFetchAllPages();

      (async function () {

        // Let original handle server fetch + DOM/filter UI update
        await originalRerender(event);

        // Hide product grid to prevent flash of unsorted content
        var listEl = facet.querySelector(PRODUCT_LIST_SEL);
        if (listEl) listEl.style.opacity = '0';

        // Re-init virtual sort with fresh data (manages its own loading bar)
        await initVirtualSort();

        // Restore parent container visibility
        listEl = facet.querySelector(PRODUCT_LIST_SEL);
        if (listEl) listEl.style.opacity = '';
      })();
    }, true); // capture phase

    facet.__vsPatch = true;
  }

  /* ───────────────────────── Browser History ───────────────────────── */

  window.addEventListener('popstate', function () {
    if (state.initialized && state.sortedCards.length > 0) {
      navigateTo(getPageFromUrl());
    }
  });

  /* ───────────────────────── Boot ───────────────────────── */

  function start() {
    patchProductFacet();
    initVirtualSort();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
